import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useCartStore } from '../store/useCartStore';
import FallbackImage from './FallbackImage';

export default function CartDrawer() {
  const { items, isOpen, toggleCart, updateQuantity, removeItem } = useCartStore();

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <>
      <div
        role="presentation"
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => toggleCart(false)}
        aria-hidden
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-[var(--background)] border-l border-gray-200 dark:border-gray-800 shadow-xl z-50 flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-[var(--foreground)]">
            Shopping Cart
          </h2>
          <button
            type="button"
            onClick={() => toggleCart(false)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            aria-label="Close cart"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="mb-4">Your cart is empty</p>
              <Link
                to="/shop"
                onClick={() => toggleCart(false)}
                className="text-green-600 hover:underline"
              >
                Continue shopping
              </Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={`${item.id}-${JSON.stringify(item.specification)}`}
                  className="flex gap-3 border-b border-gray-200 dark:border-gray-700 pb-4"
                >
                  <FallbackImage
                    src={item.image}
                    alt=""
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--foreground)] truncate">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      PKR {item.price} × {item.quantity}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity - 1)
                        }
                        className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-lg leading-none"
                      >
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity + 1)
                        }
                        className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center text-lg leading-none"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="ml-2 text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex justify-between text-[var(--foreground)] font-semibold mb-4">
              <span>Subtotal</span>
              <span>PKR {subtotal.toLocaleString()}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => toggleCart(false)}
              className="block w-full py-3 text-center bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
            >
              Checkout
            </Link>
            <button
              type="button"
              onClick={() => toggleCart(false)}
              className="block w-full py-2 mt-2 text-center text-[var(--foreground)] border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
